import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, OnChanges, signal, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from '../../../models/flow-object.model';
import { ConfigSchema, Stakeholder, StakeholderType, StakeholderTypeDescription } from '../../../models/config-schema.model';
import { InputDataTaskForm, InputDataTaskForward, InputDataTaskRegisterProcess, OutputDataTaskRegisterProcess } from '../../../models/input-output-data.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { AuthService } from '../../../services/auth.service';
import { AcessoCidadaoService } from '../../../services/acesso-cidadao.service';
import { EDocsService } from '../../../services/edocs.service';
import { MatDialog } from '@angular/material/dialog';
import { PdfPreviewDialogComponent } from '../../pdf-preview-dialog/pdf-preview-dialog.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { Papel, Processo } from '../../../models/edocs.model';
import { Utils } from '../../../shared/utils';
import { FlowDefinition, FlowInstance, FlowInstanceState } from '../../../models/flow.model';
import { CookieService } from 'ngx-cookie-service';
import { FlowObjectDefinitionService } from '../../../services/flow-object-definition.service';
import { IMPORTS } from './_imports';
import { StakeholderErrorState } from '../../../models/helper-classes.model';

@Component({
    selector: 'fob-register-process',
    templateUrl: './fob-register-process.component.html',
    styleUrl: './fob-register-process.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectRegisterProcessComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('papelRef') papelRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    StakeholderType = StakeholderType;
    StakeholderTypeDescription = StakeholderTypeDescription;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    outputData: OutputDataTaskRegisterProcess = null;
    forwardingProtocol = Enums.Messages.DefaultEmptyValue as string;
    processProtocol = Enums.Messages.DefaultEmptyValue as string;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $inputData = signal(null as InputDataTaskRegisterProcess);
    $configSchema = signal(null as ConfigSchema);
    $hasErrorsState = signal(null as StakeholderErrorState[]);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $papeisSelector = signal([] as Papel[]);
    $process = signal(null as Processo);
    $registrar = signal('');
    $previousDocuments = signal([] as any[]);
    $currentDocuments = signal([] as any[]);
    $publicMessageHtml = signal(null as string);
    $isProcessLocationInvalid = signal(false);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => Utils.isNullOrEmpty(this.$publicMessageHtml()));
    $hasPendingTask = computed(() => this.$model()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$model()?.stateId) || this.$flowInstance()?.stateId === FlowInstanceState.CancelledByDeliberation);
    $hasErrors = computed(() => this.$hasErrorsState()?.some(x => x.hasErrors));
    $dateString = computed(() => this.$model() == null ? '' : new Date(this.$model().updateDate).toLocaleString());
    $shouldLetPublicAgentInteract = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$model()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && (
            this.$flowInstance()?.ownerId != this.$currentUser()?.id
            || this.$currentUser()?.papeis?.some(x =>
                x.isCurrentUserHeadOfThisRolesOrganization
                && x.lotacaoGuid.trim().toLowerCase() == this.$flowInstance()?.currentActionableLocationId.trim().toLowerCase()
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    @Output() _cancelFlowInstanceEvent = new EventEmitter<boolean>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private cookieService: CookieService,
        private authService: AuthService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private acessoCidadaoService: AcessoCidadaoService,
        private eDocsService: EDocsService
    ) { }

    async ngOnInit() {
        this.initializeHasErrorsState();

        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        Utils.setSignal(this.$configSchema, JSON.parse(this.$flowObjectDefinition().configSchema) as ConfigSchema);
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskRegisterProcess(), JSON.parse(this.$model().inputData)));
        this.outputData = Utils.isNotNullOrEmpty(this.$model().outputData) ? Object.assign(new OutputDataTaskRegisterProcess(), JSON.parse(this.$model().outputData)) : new OutputDataTaskRegisterProcess();

        // #region [protocolo de Encaminhamento]
        this.forwardingProtocol = this.$flowInstance().rootForwardingInfo;
        // #endregion

        // #region [protocolo e autuador do Processo]
        if (Utils.isNotNullOrEmpty(this.outputData.eDocsData.processId)) {
            // caso já tenha sido autuado
            const response = await this.eDocsService.getProcesso(this.outputData.eDocsData.processId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            Utils.setSignal(this.$process, response.data);
            this.processProtocol = this.$process().protocolo;

            const response_Autuador = await this.eDocsService.getProcessoAutuador(this.outputData.eDocsData.processId);

            if (!response_Autuador.isSuccess) {
                this.toastr.error(response_Autuador.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            // resolve o nome do autuador de maneira específica
            const autuador = response_Autuador.data;
            let temp = autuador.nome;
            temp += ` - ${autuador.papel.setor.sigla}`;
            temp += ` - ${autuador.papel.setor.organizacao.sigla}`;
            temp += ` - ${autuador.papel.setor.organizacao.patriarca.sigla}`;
            this.$registrar.set(temp);
        } else {
            // caso ainda não tenha sido autuado, resolve o nome do autuador de maneira genérica
            let lastForwardingTask = this.$flowInstance().flowObjectInstances
                .filter(x => x.flowObjectDefinition.typeId == FlowObjectType.TaskForward)
                .sort((a, b) => new Date(b.createDate).toISOString().localeCompare(new Date(a.createDate).toISOString()))[0];
            this.$registrar.set((JSON.parse(lastForwardingTask.inputData) as InputDataTaskForward).recipientName);
        }
        // #endregion

        // #region [interessados do Processo]
        Utils.updSignal(this.$configSchema, x => {
            x.taskRegisterProcess.stakeholders.forEach(async y => {
                if (y.type == StakeholderType.Citizen) {
                    y = await this.resolveCitizenStakeholder(y);
                } else if (y.type == StakeholderType.Company) {
                    y = await this.resolveCompanyStakeholder(y);
                }
            });
            return x;
        });
        // #endregion

        // #region [resumo do Processo]
        this.resolveProcessSummary();
        // #endregion

        // #region [local de autuação do Processo]
        const responseAgente = await this.eDocsService.getAgente(this.$configSchema()?.taskRegisterProcess.processLocationId);
        if (!responseAgente.isSuccess) {
            this.toastr.error(responseAgente.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        let agent = responseAgente.data;
        if (!agent.ativo) {
            this.$isProcessLocationInvalid.set(true);
            this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
        }
        // #endregion

        // #region [documentos entranhados]
        const response = await this.eDocsService.getEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const encaminhamento = response.data;

        for (let item of encaminhamento.documentosEncaminhamento.filter(x => !x.controle)) {
            if (!this.$previousDocuments().some(x => x.registro == item.documento.registro)) {
                if(!item.documento.nome.endsWith('Cancelamento Manual')) {
                    Utils.updSignal(this.$previousDocuments, x => {
                        x.push({
                            id: item.documento.id,
                            registro: item.documento.registro,
                            filename: `${item.documento.nome}.${item.documento.extensao}`
                        });
                        return x;
                    });
                }
            }
        }
        // #endregion

        // #region [documentos capturados na Tarefa corrente]
        if (Utils.isNotNullOrEmpty(this.outputData)) {
            let filtered = encaminhamento.documentosEncaminhamento.filter(x =>
                x.documento.encaminhamentoId == this.outputData.eDocsData.forwardingId
                && !x.controle
            );

            for (let item of filtered) {
                if (!this.$previousDocuments().some(x => x.registro == item.documento.registro)) {
                    if(!item.documento.nome.endsWith('Cancelamento Manual')) {
                        Utils.updSignal(this.$currentDocuments, x => {
                            x.push({
                                id: item.documento.id,
                                registro: item.documento.registro,
                                filename: `${item.documento.nome}.${item.documento.extensao}`
                            });
                            return x;
                        });
                    }
                }
            }
        }
        // #endregion

        // #region [seletor de papéis]
        if (this.$shouldLetPublicAgentInteract()) {
            Utils.setSignal(this.$papeisSelector, this.$currentUser().papeis);

            // força o usuário a selecionar um papel manualmente
            setTimeout(() => Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = null;
                return x;
            }), 50);
        }

        if (this.$papeisSelector().length == 0) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            setTimeout(() => this.$publicMessageHtml.set(Utils.getParsedMessageHtml(
                this.cookieService,
                this.$flowObjectDefinition().publicMessageHtml,
                this.forwardingProtocol,
                this.processProtocol
            )), 1000);
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    async openDocumentModal(id: string) {
        this.spinner.show('flowInstance');

        const response = await this.eDocsService.getDocumentoDownloadUrl(id);

        this.spinner.hide('flowInstance');

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        setTimeout(() => {
            this.dialog.open(PdfPreviewDialogComponent, {
                data: {
                    content: response.data
                }
            });
        }, 200);
    }

    isErrorMessage(message: string): boolean {
        return Utils.isNotNullOrEmpty(message) && message[0] == '(' && message.slice(-1) == ')';
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    goToProcess() {
        const resolvedSubdomain = Utils.getResolvedEDocsSubdomain(this.cookieService);
        window.open(`https://${resolvedSubdomain}es.gov.br/p/${this.$process().protocolo}`, '_blank');
    }

    async submit() {
        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        // caso haja algum Interessado do Processo que não conseguiu ser resolvido mas pode ser ignorado
        let ignoredStakeholders = this.$configSchema().taskRegisterProcess.stakeholders.filter(x => x.shouldIgnore);
        if (ignoredStakeholders.length > 0) {
            let tempConfigSchema = JSON.parse(this.$model().flowObjectDefinition.configSchema) as ConfigSchema;
            for (let ignoredStakeholder of ignoredStakeholders) {
                tempConfigSchema.taskRegisterProcess.stakeholders.find(x => x.timestamp == ignoredStakeholder.timestamp).shouldIgnore = ignoredStakeholder.shouldIgnore;
            }

            Utils.updSignal(this.$model, x => {
                x.flowObjectDefinition.configSchema = JSON.stringify(tempConfigSchema);
                return x;
            });

            const response = await this.flowObjectDefinitionService.update(this.$model().flowObjectDefinition);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }
        }

        this._submitEvent.emit(this.$model());
    }

    getProcessSummary() {
        return this.$configSchema()?.taskRegisterProcess?.processSummary?.replaceAll('\n', '<br>');
    }

    // #region [===== private methods =====]
    // #endregion

    private async resolveCitizenStakeholder(stakeholder: Stakeholder): Promise<Stakeholder> {
        const initialName = stakeholder.name;

        if (stakeholder.isCitizenFlowInstanceOwner) {
            stakeholder.name = this.$flowInstance().ownerInfo.split(' - ')[0];
        } else if (Utils.isNotNullOrEmpty(stakeholder.citizenCpfFormField)) {
            let formData = this.getFormData();

            if (formData != null) {
                let formFieldCpfValue = formData[stakeholder.citizenCpfFormField] as string;
                if (Utils.isNotNullOrEmpty(formFieldCpfValue)) {
                    formFieldCpfValue = formFieldCpfValue.replace(/[^\d]/g, '');

                    if (Utils.isCpfValid(formFieldCpfValue)) {
                        const response_CidadaoExiste = await this.acessoCidadaoService.getCidadaoExiste(formFieldCpfValue);

                        if (!response_CidadaoExiste.isSuccess) {
                            this.toastr.error(Enums.Messages.AcApiError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        const response_CidadaoSub = await this.acessoCidadaoService.getCidadaoSub(formFieldCpfValue);

                        if (!response_CidadaoSub.isSuccess) {
                            this.toastr.error(Enums.Messages.AcApiError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        const response_RestritoCidadao = await this.acessoCidadaoService.getRestritoCidadao(response_CidadaoSub.data.sub);

                        if (!response_RestritoCidadao.isSuccess) {
                            this.toastr.error(Enums.Messages.AcApiError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                            return;
                        }

                        stakeholder.name = this.hideLastName(response_RestritoCidadao.data.nome);
                    }
                }
            }
        }

        if (stakeholder.name == initialName) {
            if (stakeholder.isMandatory) {
                Utils.updSignal(this.$hasErrorsState, x => {
                    x.find(x => x.type == 'citizenName').hasErrors = true;
                    return x;
                });

                stakeholder.name = '(erro ao obter nome do cidadão)';
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            } else {
                stakeholder.shouldIgnore = true;
            }
        }

        return stakeholder;
    }

    private async resolveCompanyStakeholder(stakeholder: Stakeholder): Promise<Stakeholder> {
        let formData = null;

        if (Utils.isNotNullOrEmpty(stakeholder.companyNameFormField)) {
            formData = formData || this.getFormData();

            if (formData != null) {
                let companyNameFormFieldValue = formData[stakeholder.companyNameFormField] as string;

                if (Utils.isNotNullOrEmpty(companyNameFormFieldValue)) {
                    stakeholder.companyName = companyNameFormFieldValue;
                } else {
                    if (stakeholder.isMandatory) {
                        Utils.updSignal(this.$hasErrorsState, x => {
                            x.find(x => x.type == 'companyName').hasErrors = true;
                            return x;
                        });

                        stakeholder.companyName = '(erro ao obter razão social)';
                        this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
                    } else {
                        stakeholder.shouldIgnore = true;
                    }
                }
            }
        } else {
            if (stakeholder.isMandatory) {
                Utils.updSignal(this.$hasErrorsState, x => {
                    x.find(x => x.type == 'companyName').hasErrors = true;
                    return x;
                });

                stakeholder.companyName = '(erro ao obter razão social)';
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            } else {
                stakeholder.shouldIgnore = true;
            }
        }

        if (Utils.isNotNullOrEmpty(stakeholder.cnpjFormField)) {
            formData = formData || this.getFormData();

            if (formData != null) {
                let cnpjFormFieldValue = formData[stakeholder.cnpjFormField] as string;

                if (Utils.isNotNullOrEmpty(cnpjFormFieldValue) && Utils.isCnpjValid(cnpjFormFieldValue)) {
                    stakeholder.cnpj = cnpjFormFieldValue;
                } else {
                    if (stakeholder.isMandatory) {
                        Utils.updSignal(this.$hasErrorsState, x => {
                            x.find(x => x.type == 'cnpj').hasErrors = true;
                            return x;
                        });

                        stakeholder.cnpj = '(erro ao obter cnpj)';
                        this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
                    } else {
                        stakeholder.shouldIgnore = true;
                    }
                }
            }
        } else {
            if (stakeholder.isMandatory) {
                Utils.updSignal(this.$hasErrorsState, x => {
                    x.find(x => x.type == 'cnpj').hasErrors = true;
                    return x;
                });

                stakeholder.cnpj = '(erro ao obter cnpj)';
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            } else {
                stakeholder.shouldIgnore = true;
            }
        }

        if (Utils.isNotNullOrEmpty(stakeholder.emailFormField)) {
            formData = formData || this.getFormData();

            if (formData != null) {
                let emailFormFieldValue = formData[stakeholder.emailFormField] as string;

                if (Utils.isNotNullOrEmpty(emailFormFieldValue) && Utils.isEmailValid(emailFormFieldValue)) {
                    stakeholder.email = emailFormFieldValue;
                } else {
                    if (stakeholder.isMandatory) {
                        Utils.updSignal(this.$hasErrorsState, x => {
                            x.find(x => x.type == 'companyEmail').hasErrors = true;
                            return x;
                        });

                        stakeholder.email = '(erro ao obter e-mail)';
                        this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
                    } else {
                        stakeholder.shouldIgnore = true;
                    }
                }
            }
        } else {
            if (stakeholder.isMandatory) {
                Utils.updSignal(this.$hasErrorsState, x => {
                    x.find(x => x.type == 'companyEmail').hasErrors = true;
                    return x;
                });

                stakeholder.email = '(erro ao obter e-mail)';
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            } else {
                stakeholder.shouldIgnore = true;
            }
        }

        return stakeholder;
    }

    private getFormData(): any {
        let formTaskDefinition = this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formTaskDefinition == null) return null;

        let formTaskInstance = this.$flowInstance().flowObjectInstances.find(x => x.flowObjectDefinitionId == formTaskDefinition.id);
        let formData = JSON.parse(formTaskInstance.inputData) as InputDataTaskForm;

        return formData.data;
    }

    private hideLastName(name: string): string {
        let firstName = name.split(' ');
        let lastName = firstName.splice(1);
        lastName = lastName.map(x => x.replace(/[a-zA-Z]/g, '*'));

        return firstName.concat(lastName).join(' ');
    }

    private resolveProcessSummary() {
        let formData = this.getFormData();
        if (formData == null) return;

        let tags = this.$configSchema().taskRegisterProcess.processSummary.match(/\{\|([\w]+)\|\}/g);
        if (tags?.length > 0) {
            let formFields = tags.map(x => x.replace(/[^\w]/g, ''));

            for (let i = 0; i < tags.length; i++) {
                Utils.updSignal(this.$configSchema, x => {
                    x.taskRegisterProcess.processSummary = x.taskRegisterProcess.processSummary
                        .replaceAll(tags[i], formData[formFields[i]] || '')
                        .replace(/ +/g, ' ').trim();
                    return x;
                });
            }
        }

        if (this.$configSchema().taskRegisterProcess.processSummary.match(/\{\|([\w]+)\|\}/g)?.length > 0) {
            setTimeout(() => this.resolveProcessSummary(), 200);
        }
    }

    private initializeHasErrorsState() {
        this.$hasErrorsState.set([
            {
                type: 'citizenName',
                hasErrors: false
            },
            {
                type: 'companyName',
                hasErrors: false
            },
            {
                type: 'cnpj',
                hasErrors: false
            },
            {
                type: 'companyEmail',
                hasErrors: false
            }
        ]);
    }
}
